import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { MagnifyingGlass, Bell, List, ArrowCounterClockwise, ArrowUp, SpinnerGap } from '@phosphor-icons/react';
import api from '@/lib/api';
import GoogleTranslate from '@/shared/GoogleTranslate';

export default function Header({ onMenuToggle }) {
  const { user, viewingUser, switchUser, resetView } = useAuth();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearch(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (q) => {
    setSearchQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 2) { setSearchResults([]); setShowSearch(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post('/auth/searchNetwork', { q });
        setSearchResults(data.data || []);
        setShowSearch(true);
      } catch { setSearchResults([]); }
      setSearching(false);
    }, 300);
  };

  const handleSelectPerson = async (person) => {
    setShowSearch(false);
    setSearchQuery('');
    await switchUser(person.ID_Cliente);
    navigate('/');
  };

  const handleReset = () => {
    resetView();
    navigate('/');
  };

  const handleGoUp = async () => {
    const parentId = viewingUser?.Parent_ID;
    if (!parentId) return;
    const realId = user?.ID_Cliente;
    if (parentId === realId) {
      // Subir para o próprio nível → reset à vista própria
      resetView();
      navigate('/');
      return;
    }
    await switchUser(parentId);
    navigate('/');
  };

  const canGoUp = !!viewingUser?.Parent_ID;

  const displayName = viewingUser ? viewingUser.NomeCompleto : (user?.NomeCompleto || 'Utilizador');
  const displayType = viewingUser ? viewingUser.TipoCliente : (user?.TipoCliente || user?.ID_TipoCliente || '');
  const displayInitial = displayName.charAt(0).toUpperCase();

  return (
    <header data-testid="header" className="h-16 bg-white border-b border-[#E5E7EB] flex items-center justify-between px-3 sm:px-4 lg:px-6 sticky top-0 z-30 gap-2">
      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
        <button onClick={onMenuToggle} className="lg:hidden p-2 rounded-md hover:bg-[#F3F4F6] flex-shrink-0" data-testid="mobile-menu-btn">
          <List size={22} />
        </button>

        {/* Network Search */}
        <div className="relative flex-1 min-w-0 max-w-[420px]" ref={searchRef}>
          <div className="flex items-center gap-2 bg-[#F3F4F6] rounded-md px-3 py-2 w-full">
            <MagnifyingGlass size={18} className="text-[#6B7280] flex-shrink-0" />
            <input
              data-testid="network-search"
              type="text"
              placeholder="Pesquisar na rede..."
              className="bg-transparent border-none outline-none text-sm w-full text-[#111827] placeholder:text-[#6B7280] min-w-0"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => { if (searchResults.length > 0) setShowSearch(true); }}
            />
            {searching && <SpinnerGap size={14} className="animate-spin text-[#6B7280] flex-shrink-0" />}
          </div>

          {showSearch && searchResults.length > 0 && (
            <div data-testid="search-dropdown" className="absolute top-full left-0 mt-1 w-full bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-[400px] overflow-y-auto z-50">
              <div className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] font-medium text-[#6B7280] bg-[#F9FAFB]">
                Rede ({searchResults.length})
              </div>
              {searchResults.map(person => (
                <button
                  key={person.ID_Cliente}
                  data-testid={`search-result-${person.ID_Cliente}`}
                  onClick={() => handleSelectPerson(person)}
                  className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#F3F4F6] transition-colors border-b border-[#F3F4F6] last:border-0"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-[#111827]">{person.NomeCompleto}</span>
                    <span className="text-[9px] uppercase tracking-[0.15em] font-semibold px-1.5 py-0.5 rounded border bg-blue-50 text-[#0F1829] border-[#0F1829]/20">{person.TipoCliente}</span>
                  </div>
                  {person.Email && <span className="text-[#6B7280] text-xs block">{person.Email}</span>}
                  {person.linked_to && (
                    <span className="text-[#978D78] text-[10px] mt-0.5 block" data-testid={`search-result-${person.ID_Cliente}-linked`}>
                      <span className="uppercase tracking-wider mr-1">Ligado a:</span>
                      {person.linked_to.NomeCompleto || `#${person.linked_to.ID_Cliente}`}
                      {person.linked_to.ID_TipoCliente && ` (${person.linked_to.ID_TipoCliente})`}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
          {showSearch && searchResults.length === 0 && searchQuery.length >= 2 && !searching && (
            <div className="absolute top-full left-0 mt-1 w-full bg-white border border-[#E5E7EB] rounded-lg shadow-lg z-50">
              <div className="px-3 py-4 text-sm text-[#6B7280] text-center">Sem resultados na rede</div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
        <GoogleTranslate className="hidden md:block" />
        {/* Viewing indicator */}
        {viewingUser && canGoUp && (
          <button data-testid="go-up-btn" onClick={handleGoUp}
            title={`Subir para ${viewingUser.Parent_Nome || 'pai'}`}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#FAF8F2] text-[#0F1829] border border-[#E6E1D6] rounded-md hover:bg-[#F3F0E7] transition-colors">
            <ArrowUp size={14} /> Subir
          </button>
        )}
        {viewingUser && canGoUp && (
          <button data-testid="go-up-btn-mobile" onClick={handleGoUp}
            title={`Subir para ${viewingUser.Parent_Nome || 'pai'}`}
            className="sm:hidden p-2 rounded-md bg-[#FAF8F2] text-[#0F1829] border border-[#E6E1D6] hover:bg-[#F3F0E7]">
            <ArrowUp size={16} />
          </button>
        )}
        {viewingUser && (
          <button data-testid="reset-view-btn" onClick={handleReset}
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 rounded-md hover:bg-amber-100 transition-colors">
            <ArrowCounterClockwise size={14} /> Voltar à minha vista
          </button>
        )}
        {viewingUser && (
          <button data-testid="reset-view-btn-mobile" onClick={handleReset}
            title="Voltar à minha vista"
            className="sm:hidden p-2 rounded-md bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100">
            <ArrowCounterClockwise size={16} />
          </button>
        )}

        <button className="hidden sm:flex p-2 rounded-md hover:bg-[#F3F4F6] relative" data-testid="notifications-btn">
          <Bell size={20} className="text-[#6B7280]" />
        </button>
        <div className="flex items-center gap-2 pl-2 sm:pl-3 sm:border-l border-[#E5E7EB]">
          {(() => {
            // Avatar: own photo (HasPhoto) or the photo of the consultor being viewed.
            const avatarId = viewingUser ? viewingUser.ID_Cliente : (user?.HasPhoto ? user.ID_Cliente : null);
            if (!avatarId) return null;
            const src = `${""}/api/profile/photo/${avatarId}?uid=${avatarId}`;
            return (
              <img
                src={src}
                alt={displayName}
                data-testid="header-avatar-img"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const sib = e.currentTarget.nextElementSibling;
                  if (sib) sib.style.display = 'flex';
                }}
                className="w-8 h-8 rounded-md object-cover border border-[#E5E7EB]"
              />
            );
          })()}
          <div
            data-testid="header-avatar-initial"
            style={{ display: (viewingUser || user?.HasPhoto) ? 'none' : 'flex' }}
            className={`w-8 h-8 rounded-md items-center justify-center text-white text-xs font-bold ${viewingUser ? 'bg-amber-500' : 'bg-[#0F1829]'}`}
          >
            {displayInitial}
          </div>
          <div className="hidden md:block">
            <p className="text-sm font-medium text-[#111827] leading-none">{displayName}</p>
            <p className="text-xs text-[#6B7280] mt-0.5">{displayType}</p>
          </div>
        </div>
      </div>
    </header>
  );
}
